import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import { Icon } from '@fluentui/react/lib/Icon';
import { PatchType } from '../../../store/appState';
import { App, SignInState } from '../../../app';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { forMinWidth } from '../../../theme/breakpoints';
import Panel from '../../shared/Panel';
import { useHistory } from 'react-router';
import * as Actions from '../../../store/actions';
import { Permission, UserRole } from '../../../config/permissions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import TicketEditor, { TicketEditorFields, TicketUpdateFields } from './TicketEditor';
import { hasItems } from '../../../utils/collections';
import { TicketContext } from './TicketContext';
import { keepFormOpen, ticketOperationKey, useOperation } from '../../../store/operation';
import OperationNotification from './OperationNotification';
import RichTicketDescription from './RichTicketDescription';
import { DetailViewType, hashPrefixTicket, hashPrefixTicketPinnedItems, UrlParam } from '../../../constants/urlParams';
import { useQueryParam } from '../../../hooks/routing';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import FeedbackTimelineComponent from './FeedbackTimelineComponent';
import { PostType, RootPost, StateTag } from '../../../RootPost';
import { UserProperties } from '../../../UserProperties';
import HtmlPreview from '../../shared/HtmlPreview';
import AISummaryView from './AISummaryView';

export interface ContextualMenuItem extends IContextualMenuItem {
    data?: {
        requiredPermission?: Permission;
    };
}

export interface RichPostDetailProps {
    signInState?: SignInState;
    hideVotes?: boolean;
    userRoles: UserRole[];
}

export default function RichPostDetail(props: RichPostDetailProps): JSX.Element {
    const { signInState, userRoles } = props;

    const ticketDetailsCtx = useContext(TicketContext);
    const ticket = ticketDetailsCtx.ticketDetails.ticket?.data;
    const userProperties = useSelector((state: RootState) => state.appState.userProperties);
    const aiSummary = useSelector((state: RootState) => state.appState.aiSummary);

    const history = useHistory();

    const detailViewType = useQueryParam(UrlParam.DetailViewType);
    const [selectedKey, setSelectedKey] = React.useState(detailViewType === DetailViewType.solutions ? DetailViewType.solutions : DetailViewType.all);

    const ticketOpKey = ticketOperationKey(ticket?.id);
    const ticketOperation = useOperation(ticketOpKey);

    const editTicketOpKey = ticketOperationKey(ticket?.id, PatchType.ModifyTicket);
    const editTicketOperation = useOperation(editTicketOpKey);

    const editTicketTagsOpKey = ticketOperationKey(ticket?.id, PatchType.ModifyTicketTags);
    const editTicketTagsOperation = useOperation(editTicketTagsOpKey);

    const isFollowing = ticketDetailsCtx.ticketDetails.ticket?.data?.isFollowing ?? null;

    const rawDescription = ticket?.text;

    const handleSaveTicket = (updatedFields: TicketUpdateFields): void => {
        Actions.updateTicket(ticket.id, editTicketOpKey, updatedFields.title, updatedFields.description, updatedFields.addedTags, updatedFields.removedTags);
    }

    const handlePivotClick = (item?: PivotItem, ev?: React.MouseEvent<HTMLElement>): void => {
        const params = new URLSearchParams(history.location.search);
        params.set(UrlParam.DetailViewType, item.props.itemKey);
        history.push(`${history.location.pathname}?${params}`);
        if (item) {
            setSelectedKey(item.props.itemKey! as DetailViewType);
        }
    }

    const showNeedMoreInfoLink = ticketDetailsCtx?.ticketDetails?.originalNmiComment?.id && isNeedMoreInfoVisible(ticket, userProperties);

    const totalPostsCount = (ticketDetailsCtx?.ticketDetails?.ticket?.data?.commentCount ?? 0)
        + (ticketDetailsCtx?.ticketDetails?.ticket?.data?.solutionCount ?? 0);

    return <PostDetailWrapper>
        {!ticket && <div>Invalid feedback item.</div>}

        <OperationNotificationWrapper
            operation={ticketOperation.operation}
            resetOperation={ticketOperation.resetOperation}
            className="top-level-text"
        />

        <OperationNotificationWrapper
            operation={editTicketOperation.operation}
            resetOperation={editTicketOperation.resetOperation}
            className="top-level-text"
        />

        <OperationNotificationWrapper
            operation={editTicketTagsOperation.operation}
            resetOperation={editTicketTagsOperation.resetOperation}
            className="top-level-text"
        />

        {ticket && keepFormOpen(editTicketOperation.operation) && <>
            <TicketEditor
                trackingId={ticket.trackingId}
                fields={({
                    title: ticket.title,
                    description: rawDescription,
                    tags: ticket.tags
                })}
                forSuggestion={ticket.type === PostType.suggestion}
                onSave={handleSaveTicket}
                onCancel={() => editTicketOperation.resetOperation()}
            />
        </>}

        {ticket && !keepFormOpen(editTicketOperation.operation) && <PagePanel>
            <MainContentWrapper>
                <MainContent>
                    <RichTicketDescription
                        ticket={ticket}
                        userRoles={userRoles}
                        userProperties={userProperties}
                        isFollowing={isFollowing}
                        isTicketReadOnly={ticketDetailsCtx?.ticketDetails?.isTicketReadOnly}
                        lastStateChangeDaysAgo={ticketDetailsCtx?.ticketDetails.lastStateChangeDaysAgo}
                        hideVotes={props.hideVotes}
                        signInState={signInState}
                        solutionElementId={hasItems(ticketDetailsCtx.ticketDetails?.pinnedItems) && "pinned-items"}
                        mostRelevantFixedInRelease={ticketDetailsCtx?.ticketDetails?.mostRelevantFixedInRelease}
                        commentCount={ticketDetailsCtx?.ticketDetails?.ticket?.data?.commentCount}
                        showNeedMoreInfoLink={showNeedMoreInfoLink}
                        fillMissingInfoClicked={ticketDetailsCtx?.ticketDetails?.fillMissingInfoClicked}
                    />

                    {aiSummary && <AISummaryView ticket={ticket} />}

                    {hasItems(ticketDetailsCtx.ticketDetails?.pinnedItems) && (
                        <PinnedItems id="pinned-items">
                            <span className="pin-icon">
                                <Icon iconName="pinned" />
                                <span>&nbsp;&nbsp;Pinned</span>
                            </span>
                            <div className="pin-body">
                                <FeedbackTimelineComponent
                                    idPrefix={hashPrefixTicketPinnedItems}
                                    currentUserId={ticket.currentUserId}
                                    timelineDepth={0}
                                    timelineItems={ticketDetailsCtx.ticketDetails?.pinnedItems ?? []}
                                    forbidAddNodeOperations
                                    parentNodeId={ticket?.id}
                                />
                            </div>
                        </PinnedItems>
                    )}

                    {ticket?.type === PostType.problem && (
                        <div>
                            <PivotsWrapper>
                                <span className="view-by message-light">View timeline by</span>
                                <Pivot
                                    selectedKey={selectedKey}
                                    onLinkClick={handlePivotClick}
                                    className="pivot"
                                    styles={{
                                        root: { marginLeft: "6.8rem" },
                                        linkContent: { color: "var(--color-on-surface-link)" }
                                    }}
                                >
                                    <PivotItem itemKey={DetailViewType.all} headerText={`All Posts (${totalPostsCount})`} />
                                    <PivotItem itemKey={DetailViewType.solutions} headerText={`Solutions & workarounds (${ticketDetailsCtx.ticketDetails?.solutionTimelineItems?.length})`} />
                                </Pivot>
                            </PivotsWrapper>

                            {selectedKey === DetailViewType.all &&
                                <PivotItem itemKey={DetailViewType.all} headerText={`All Posts (${totalPostsCount})`}>
                                    <TimelineComponentWrapper>
                                        <FeedbackTimelineComponent
                                            idPrefix={hashPrefixTicket}
                                            timelineDepth={0}
                                            timelineItems={ticketDetailsCtx.ticketDetails?.timelineItems ?? []}
                                            canContainComments
                                            canContainSolutions
                                            currentUserId={ticket.currentUserId}
                                            parentNodeId={ticket?.id}
                                            showNeedMoreInfoLink={showNeedMoreInfoLink}
                                            fillMissingInfoClicked={ticketDetailsCtx?.ticketDetails?.fillMissingInfoClicked}
                                        />
                                    </TimelineComponentWrapper>
                                </PivotItem>
                            }

                            {selectedKey === DetailViewType.solutions &&
                                <PivotItem itemKey={DetailViewType.solutions} headerText={`Solutions & workarounds (${ticketDetailsCtx.ticketDetails?.solutionTimelineItems?.length})`}>
                                    <TimelineComponentWrapper>
                                        <FeedbackTimelineComponent
                                            idPrefix={hashPrefixTicket}
                                            timelineDepth={0}
                                            timelineItems={ticketDetailsCtx.ticketDetails?.solutionTimelineItems ?? []}
                                            canContainComments
                                            canContainSolutions
                                            currentUserId={ticket.currentUserId}
                                            parentNodeId={ticket?.id}
                                            showNeedMoreInfoLink={showNeedMoreInfoLink}
                                            fillMissingInfoClicked={ticketDetailsCtx?.ticketDetails?.fillMissingInfoClicked}
                                        />
                                    </TimelineComponentWrapper>
                                </PivotItem>
                            }
                        </div>
                    )}

                    {ticket?.type !== PostType.problem && (
                        <TimelineComponentWrapper>
                            <FeedbackTimelineComponent
                                idPrefix="T"
                                timelineDepth={0}
                                timelineItems={ticketDetailsCtx.ticketDetails?.timelineItems ?? []}
                                canContainComments
                                canContainSolutions
                                currentUserId={ticket.currentUserId}
                                parentNodeId={ticket?.id}
                            />
                        </TimelineComponentWrapper>
                    )}
                </MainContent>
            </MainContentWrapper>
        </PagePanel>}
    </PostDetailWrapper >
}

const isNeedMoreInfoVisible = (item: RootPost, userProperties: UserProperties): boolean => {
    return item != null &&
        (
            item.stateTagId === StateTag.more_info
            // @TODO: We might need the commented code again after bug 556370 is fixed in backend
            // || userProperties.needMoreInfoFromMe[item.trackingId] === true
        ) &&
        !isMoreInfoProvided(item, userProperties);
}

export const isMoreInfoProvided = (item: RootPost, userProperties: UserProperties): boolean => {
    return ((item != null) &&
        (userProperties.needMoreInfoFromMe[item.trackingId] === false));
}

const MainContentWrapper = styled.div`
`;

const PostDetailWrapper = styled.div`
    display: grid;
    grid-template-columns: 100%;
    grid-gap: 1.5rem;
    line-height: 140%;
    margin: 0;

    ${forMinWidth.md`
        margin: 2rem var(--page-margin-horizontal) 0 var(--page-margin-horizontal);
    `}

    button {
        white-space: nowrap;
    }

    .message-light {
        color: var(--color-on-surface-light);
    }

    .view-by {
        white-space: nowrap;
    }

    .post-state {
        padding: 0 .5rem;
        white-space: nowrap;
    }
`;

const MainContent = styled.div`
    grid-area: main-content;
    position: relative;
`;

const PagePanel = styled(Panel)`
    margin: 0;
    padding: 0;
    padding-bottom: 1rem;
`;

const TimelineComponentWrapper = styled.div`
    padding: 3rem 0.5rem 2rem 0.5rem;

    ${forMinWidth.md`
        padding: 3rem 2rem 2rem 4rem;
    `}
`;

const PivotsWrapper = styled.div`
    display: flex;
    align-items: baseline;
    flex-direction: column;
    position: relative;
    padding-left: 1.5rem;

    .pivot {
        width: 100%;
    }

    .ms-Pivot {
        margin-left: 0;
    }

    .view-by {
        font-size: 1.2rem;
        margin-left: 1rem;
    }

    .ms-Pivot-link {
        font-size: 1.2rem;
        margin-right: 0;
    }

    ${forMinWidth.md`
        flex-direction: row;

        .ms-Pivot {
            margin-left: 1rem;
        }

        .ms-Pivot-link {
            font-size: 1.4rem;
            margin-right: 0.8rem;
        }

        .view-by {
            font-size: 1.4rem;
            top: 1.2rem;
        }
    `}
`;

const PinnedItems = styled.div`
    --timeline-node-body-background-color: var(--color-surface);

    padding: 1rem 0 0 2rem;
    margin-bottom: 2.5rem;
    position: relative;
    background-color: var(--color-background);

    ${forMinWidth.md`
        margin: .5rem 2rem 2.5rem;
    `}

    .pin-icon {
        display: block;
        margin: 0 0 1rem 0;
        font-size: 1.5rem;
    }

    .pin-body {
        display: block;
        padding: 2rem 1rem 1rem 1rem;

        ${forMinWidth.md`
            padding: 2rem 2rem 1rem 1rem;
        `}
    }
`;

const OperationNotificationWrapper = styled(OperationNotification)`
    margin: .5rem 0;
`;